import { useState, useRef } from 'react';
import {
  useCreateExternalTraining,
  type CreateExternalTrainingInput,
} from '@/api/external-trainings';
import { usePresignedUrl } from '@/api/evidence';
import { cn } from '@/lib/utils';
import { X, Upload } from 'lucide-react';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

export function AddTrainingDialog({ onClose }: { onClose: () => void }) {
  const createTraining = useCreateExternalTraining();
  const presignedUrl = usePresignedUrl();
  const fileRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [institution, setInstitution] = useState('');
  const [description, setDescription] = useState('');
  const [completedAt, setCompletedAt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const busy = uploading || createTraining.isPending;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      setErrorMsg('El archivo no puede superar los 50 MB.');
      e.target.value = '';
      return;
    }
    setFile(f);
    setErrorMsg('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setErrorMsg('');

    try {
      let fileUrl: string | undefined;
      let fileName: string | undefined;

      // Upload file if selected
      if (file) {
        setUploading(true);
        const res = await presignedUrl.mutateAsync({
          file_name: file.name,
          file_type: file.type || 'application/octet-stream',
          file_size: file.size,
          folder: 'external-trainings',
        });
        const { upload_url, download_url } = res.data;
        const uploadRes = await fetch(upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!uploadRes.ok) throw new Error('Error al subir archivo al almacenamiento');
        fileUrl = download_url;
        fileName = file.name;
        setUploading(false);
      }

      const payload: CreateExternalTrainingInput = {
        title: title.trim(),
        ...(institution.trim() && { institution: institution.trim() }),
        ...(description.trim() && { description: description.trim() }),
        ...(completedAt && { completed_at: completedAt }),
        ...(fileUrl && { file_url: fileUrl }),
        ...(fileName && { file_name: fileName }),
      };

      await createTraining.mutateAsync(payload);
      onClose();
    } catch (err) {
      setUploading(false);
      const msg =
        err instanceof Error
          ? err.message
          : 'Error al registrar la capacitación. Intenta de nuevo.';
      setErrorMsg(msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        {/* Dialog header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-card-foreground">Nueva Capacitación Externa</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {/* Title */}
          <div>
            <label className="mb-1 block text-sm font-medium text-card-foreground">
              Título <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={300}
              placeholder="Nombre de la capacitación"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Institution */}
          <div>
            <label className="mb-1 block text-sm font-medium text-card-foreground">
              Institución
            </label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              maxLength={300}
              placeholder="Nombre de la institución"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-card-foreground">
              Descripción
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              rows={3}
              placeholder="Breve descripción de la capacitación"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Completed at */}
          <div>
            <label className="mb-1 block text-sm font-medium text-card-foreground">
              Fecha de finalizacion
            </label>
            <input
              type="date"
              value={completedAt}
              onChange={(e) => setCompletedAt(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* File upload */}
          <div>
            <label className="mb-1 block text-sm font-medium text-card-foreground">
              Archivo de soporte
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-2 text-sm font-medium text-card-foreground transition-colors hover:bg-accent"
              >
                <Upload className="h-4 w-4" />
                {file ? 'Cambiar' : 'Seleccionar'}
              </button>
              {file && <span className="truncate text-xs text-muted-foreground">{file.name}</span>}
              <input
                ref={fileRef}
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.docx,.pptx,.jpg,.jpeg,.png,.webp"
                className="hidden"
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              PDF, DOCX, PPTX, JPG, PNG. Max 50 MB.
            </p>
          </div>

          {/* Error */}
          {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-card-foreground transition-colors hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy || !title.trim()}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90',
                busy && 'cursor-not-allowed opacity-60',
              )}
            >
              {uploading
                ? 'Subiendo archivo...'
                : createTraining.isPending
                  ? 'Guardando...'
                  : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
